import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { CalendarDays, Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { PageHeader } from '../../components/shared/PageHeader';
import { LoadingSkeleton } from '../../components/shared/LoadingSkeleton';
import { ConfirmDialog } from '../../components/shared/ConfirmDialog';
import { Link } from 'react-router-dom';

interface Batch {
  id: number;
  name: string;
  course_name: string;
  class_name: string;
  section_name: string;
  teacher_name: string;
  batch_time: string;
  capacity: number;
  status: string;
}

export default function BatchesPage() {
  const queryClient = useQueryClient();
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['batches'],
    queryFn: async () => api.get<any>('/api/batches'),
  });

  const batches: Batch[] = data?.data || [];

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => api.delete(`/api/batches/${id}`),
    onSuccess: () => {
      toast.success('Batch deleted');
      setDeleteConfirm(null);
      queryClient.invalidateQueries({ queryKey: ['batches'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete batch');
    },
  });

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageHeader
        icon={CalendarDays}
        iconColor="text-cyan-500"
        title="Batches"
        subtitle="Manage course batches"
        actionTo="/batches/create"
        actionLabel="Add Batch"
      />

      {isLoading ? (
        <LoadingSkeleton count={6} />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4">
          {batches.map((batch: Batch, index: number) => (
            <motion.div
              key={batch.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="glass-card p-3 sm:p-5 rounded-2xl"
            >
              <div className="flex items-start justify-between mb-2 sm:mb-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl gradient-cyan flex items-center justify-center">
                  <CalendarDays className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <div className="flex gap-1">
                   <Link to={`/batches/${batch.id}/edit`}>
                     <Button variant="ghost" size="icon" className="h-8 w-8">
                       <Edit className="w-4 h-4" />
                     </Button>
                   </Link>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-light-text-muted hover:text-rose-500"
                    onClick={() => setDeleteConfirm(batch.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <h3 className="text-sm sm:text-lg font-bold text-light-text dark:text-dark-text">{batch.name}</h3>
              <p className="text-xs sm:text-sm text-light-text-muted">{batch.course_name}</p>
              <div className="mt-2 sm:mt-3 space-y-1">
                <p className="text-[10px] sm:text-xs text-light-text-secondary">{batch.class_name} {batch.section_name && `• ${batch.section_name}`}</p>
                {batch.teacher_name && <p className="text-[10px] sm:text-xs text-light-text-secondary">Teacher: {batch.teacher_name}</p>}
              </div>
              <div className="mt-2 sm:mt-3 flex items-center gap-2 flex-wrap">
                <span className="text-[10px] sm:text-xs px-2 py-1 rounded-full bg-cyan-100 dark:bg-cyan-500/20 text-cyan-700 dark:text-cyan-300">
                  Capacity: {batch.capacity}
                </span>
                <span className={`text-[10px] sm:text-xs px-2 py-1 rounded-full ${batch.status === 'active' ? 'bg-lime-100 dark:bg-lime-500/20 text-lime-700 dark:text-lime-300' : 'bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300'}`}>
                  {batch.status}
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={deleteConfirm !== null}
        onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}
        title="Delete Batch"
        onConfirm={() => deleteMutation.mutate(deleteConfirm!)}
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
